import { queryClient } from '@/config';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { getSyncManagementQueryKey } from '@/query';
import { useBroadCast } from '@/shared/stores/broadcast-personal';
import { useIdentificationManagementStore } from '@/shared/stores/execute-identification-management';
import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { DataSyncService } from './sync-managements/data-sync-service';
import { SyncIdentificationData } from './sync-managements/database-service';
import {
  NO_ANIMAL_AVAILABLE_CODE,
  NoAnimalAvailableError,
  identificationPipeline
} from './sync-managements/identification-pipeline';
import { OfflineDataDexieService } from './sync-managements/offline-data-service';

const offlineDataService = new OfflineDataDexieService();

const PING_INTERVAL_MS = 5000;
const PING_TIMEOUT_MS = 20000;
const PING_TIMEOUT_CLOSE_CODE = 4000;

interface ConnectWebsocket {
  host?: string;
  port?: string;
  url?: string;
}

function buildWsUrl(data: ConnectWebsocket): string | null {
  if (data.url) {
    const trimmed = data.url.trim();
    // Normaliza http(s):// → ws(s):// e adiciona wss:// como default se vier sem protocolo
    if (/^wss?:\/\//i.test(trimmed)) return trimmed;
    if (/^https:\/\//i.test(trimmed)) return trimmed.replace(/^https:\/\//i, 'wss://');
    if (/^http:\/\//i.test(trimmed)) return trimmed.replace(/^http:\/\//i, 'ws://');
    return `wss://${trimmed}`;
  }
  if (data.host && data.port) {
    return `ws://${data.host}:${data.port}`;
  }
  return null;
}

interface WSResponse {
  type: string;
  message: SyncIdentificationData;
  clientId: string;
}

interface IContextData {
  onSubmit: (data: ConnectWebsocket) => void;
}

export const Context = createContext({} as IContextData);

export function BroadcastPersonal({ children }: { children: React.ReactNode }) {
  const { setStatus, setSocket, setSocketConnected, setIsWaitingMasterConfirmation } = useBroadCast();
  const { onSuccess, setLastOxManaged } = useIdentificationManagementStore();
  const [_, setOfflineData] = useLocalStorage<string | null>('clean-offline-data', null);

  const syncServiceRef = useRef<DataSyncService>();
  const pingIntervalRef = useRef<number | null>(null);
  const pingTimeoutRef = useRef<number | null>(null);
  const awaitingPongRef = useRef(false);

  const stopPingPong = useCallback(() => {
    if (pingIntervalRef.current !== null) {
      window.clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (pingTimeoutRef.current !== null) {
      window.clearTimeout(pingTimeoutRef.current);
      pingTimeoutRef.current = null;
    }

    awaitingPongRef.current = false;
  }, []);

  const startPingPong = useCallback(
    (socket: WebSocket) => {
      stopPingPong();

      const sendPing = () => {
        if (socket.readyState !== WebSocket.OPEN) return;
        if (awaitingPongRef.current) return;

        awaitingPongRef.current = true;

        socket.send(
          JSON.stringify({
            type: 'ping',
            timestamp: Date.now()
          })
        );

        pingTimeoutRef.current = window.setTimeout(() => {
          if (!awaitingPongRef.current) return;
          if (socket.readyState !== WebSocket.OPEN) return;

          stopPingPong();
          try {
            socket.close(PING_TIMEOUT_CLOSE_CODE, 'ping-timeout');
          } catch {
            setSocketConnected(false);
            setSocket(null);
          }
        }, PING_TIMEOUT_MS);
      };

      sendPing();
      pingIntervalRef.current = window.setInterval(sendPing, PING_INTERVAL_MS);
    },
    [setSocket, setSocketConnected, stopPingPong]
  );

  // Slave broadcast
  const onSubmit = useCallback(
    (data: ConnectWebsocket) => {
      try {
        const wsUrl = buildWsUrl(data);
        if (!wsUrl) {
          console.error('[SLAVE] dados de conexão inválidos', data);
          toast.error('Dados de conexão inválidos');
          return;
        }

        console.log('[SLAVE] conectando ao master', { wsUrl, mode: data.url ? 'external' : 'lan' });
        const socket = new WebSocket(wsUrl);

        setSocket(socket);

        socket.onopen = () => {
          console.log('[SLAVE] socket OPEN');
          setSocketConnected(true);
          startPingPong(socket);
        };

        socket.onclose = (event) => {
          console.warn('[SLAVE] socket CLOSE', { code: event.code, reason: event.reason });
          stopPingPong();
          setSocketConnected(false);
          setSocket(null);

          if (event.code === PING_TIMEOUT_CLOSE_CODE) {
            toast.error('Rede de manejo sem resposta');
          } else {
            toast.error('Rede de manejo desconectada');
          }

          // setTimeout(() => {
          //   handleEventRoom(data, channelSelected, versionSelected);
          // }, 3000);
        };

        socket.onerror = () => {
          console.error('[SLAVE] socket ERROR');
          stopPingPong();
          // if (socketConnected) return

          // setSocketConnection(false);
          // console.log('Erro de WebSocket:', error);
          setSocketConnected(false);
          setSocket(null);

          toast.error('Rede de manejo desconectada');
        };

        socket.onmessage = async (event) => {
          try {
            const response = JSON.parse(event.data) as any;

            if (response?.type === 'pong') {
              awaitingPongRef.current = false;
              if (pingTimeoutRef.current !== null) {
                window.clearTimeout(pingTimeoutRef.current);
                pingTimeoutRef.current = null;
              }
              return;
            }

            console.log(`[SLAVE] WS recebeu ${response.type}`, {
              sisbov: response.payload?.sisbov,
              oxId: response.payload?.oxId
            });

            if (response.type === 'welcome') {
              console.log('[SLAVE] welcome -> enviando sync-slave');
              socket.send(
                JSON.stringify({
                  type: 'sync-slave',
                  message: 'not synced'
                })
              );
            }

            if (response.type === 'sync-slave') {
              console.log('[SLAVE] sync-slave: itens recebidos', response.payload?.length ?? 0);
              for await (const item of response.payload) {
                await syncServiceRef.current?.upsertSisbov(item.data);
              }

              setLastOxManaged();
            }

            if (response.type === 'master-received') {
              await syncServiceRef.current?.upsertSisbov(response.payload);

              // Lê o estado atual do store diretamente para evitar closure stale no useCallback
              const {
                pendingSisbov: currentPending,
                pendingElectronic: currentPendingElectronic,
                setPendingSisbov: setCurrentPending,
                setPendingElectronic: setCurrentPendingElectronic
              } = useBroadCast.getState();
              const isExpected =
                (currentPending !== null && response.payload?.sisbov === currentPending) ||
                (currentPendingElectronic !== null && response.payload?.electronic === currentPendingElectronic);

              console.log('[SLAVE] master-received', {
                sisbov: response.payload?.sisbov,
                electronic: response.payload?.electronic,
                oxId: response.payload?.oxId,
                pendingSisbov: currentPending,
                pendingElectronic: currentPendingElectronic,
                isExpected
              });

              if (isExpected) {
                // Master confirmou exatamente o animal que o slave enviou:
                // limpa o loading e reseta as etapas do manejo
                console.log('[SLAVE] confirmação do MEU animal -> limpa loading + reset etapas');
                setCurrentPending(null);
                setCurrentPendingElectronic(null);
                setIsWaitingMasterConfirmation(false);
                onSuccess({ skip: false });
              } else {
                // Master confirmou outro animal: só atualiza o lastOxManaged,
                // as etapas do slave ficam como estão
                console.log('[SLAVE] confirmação de OUTRO animal -> apenas atualiza lastOxManaged');
                onSuccess({ skip: true });
              }
            }

            if (response.type === 'master-broadcast') {
              // Master processou um animal próprio: sincroniza o dado e atualiza o lastOxManaged,
              // mas as etapas do slave ficam como estão
              console.log('[SLAVE] master-broadcast (animal do master) -> upsertSisbov local', {
                sisbov: response.payload?.sisbov,
                oxId: response.payload?.oxId
              });
              await syncServiceRef.current?.upsertSisbov(response.payload);
              onSuccess({ skip: true });
            }

            if (response.type === 'master-error') {
              const {
                pendingSisbov: currentPending,
                pendingElectronic: currentPendingElectronic,
                setPendingSisbov: setCurrentPending,
                setPendingElectronic: setCurrentPendingElectronic
              } = useBroadCast.getState();
              const isExpected =
                (currentPending !== null && response.payload?.sisbov === currentPending) ||
                (currentPendingElectronic !== null && response.payload?.electronic === currentPendingElectronic);
              const isNoAnimalAvailable = response.payload?.code === NO_ANIMAL_AVAILABLE_CODE;

              console.warn('[SLAVE] master-error recebido', {
                sisbov: response.payload?.sisbov,
                electronic: response.payload?.electronic,
                code: response.payload?.code,
                message: response.payload?.message,
                pendingSisbov: currentPending,
                pendingElectronic: currentPendingElectronic,
                isExpected,
                isNoAnimalAvailable
              });

              if (isExpected) {
                setCurrentPending(null);
                setCurrentPendingElectronic(null);
                setIsWaitingMasterConfirmation(false);

                if (response.payload?.sisbov) {
                  console.log('[SLAVE] limpando registro other rejeitado', {
                    sisbov: response.payload.sisbov
                  });
                  await syncServiceRef.current?.deleteRejectedOther(response.payload.sisbov);
                } else if (response.payload?.electronic) {
                  console.log('[SLAVE] limpando registro other rejeitado (eletrônico)', {
                    electronic: response.payload.electronic
                  });
                  await syncServiceRef.current?.deleteRejectedOtherByElectronic(response.payload.electronic);
                }

                if (isNoAnimalAvailable) {
                  console.log('[SLAVE] sem animais disponíveis -> abre dialog de troca de GTA');
                  useIdentificationManagementStore.getState().setNoAnimalAvailable(true);
                } else {
                  toast.error(response.payload?.message || 'Erro ao processar identificação no master');
                }
              }
            }

            if (response.type === 'delete') {
              console.log('[SLAVE] delete recebido', { sisbov: response.payload?.sisbov });
              await syncServiceRef.current?.deleteSisbov(response.payload.sisbov, () => {
                setOfflineData('clean');
              });

              queryClient.invalidateQueries({ queryKey: [getSyncManagementQueryKey] });
            }
          } catch (error) {
            console.error('[SLAVE] Erro ao processar mensagem:', error);
          }
        };
      } catch (error) {
        console.error('Erro ao criar conexão WebSocket:', error);
      }
    },
    [setOfflineData, setSocket, setSocketConnected, startPingPong, stopPingPong]
  );

  // Master broadcast
  const onMessageReceived = useCallback(
    async (data: WSResponse) => {
      console.log('[MASTER] onMessageReceived', {
        type: data.type,
        clientId: data.clientId,
        sisbov: data.message?.sisbov,
        oxId: data.message?.oxId
      });

      if (data.type === 'sync-slave') {
        if (!syncServiceRef.current) return;

        const dataToSync = await syncServiceRef.current.syncAllData();

        console.log('[MASTER] sync-slave -> enviando dados ao client', {
          clientId: data.clientId,
          itemsCount: dataToSync?.length ?? 0
        });

        if (dataToSync && dataToSync.length > 0) {
          window.electronAPI.webSocket.sendToClient(data.clientId, 'sync-slave', dataToSync);
        }
      }

      if (data.type === 'slave-broadcast') {
        // Lê os animais da GTA diretamente do offline-data (sincronizado na tela
        // de listagem de manejo). Não depende do master estar na tela de identificação.
        const oxenForGta = await offlineDataService.getSimpleAnimalsByGtaId(data.message.gtaId);
        const ageInMonths = Number((data.message as any).ageInMonths) || 0;
        const hasContext = oxenForGta.length > 0 && ageInMonths > 0;

        console.log('[MASTER] slave-broadcast recebido', {
          clientId: data.clientId,
          sisbov: data.message?.sisbov,
          oxId: data.message?.oxId,
          gtaId: data.message?.gtaId,
          ageInMonths,
          oxenForGtaCount: oxenForGta.length,
          hasContext
        });

        identificationPipeline
          .submit({
            payload: data.message,
            source: 'slave-ws',
            context: hasContext
              ? {
                  oxen: oxenForGta,
                  gtaId: data.message.gtaId,
                  age: ageInMonths
                }
              : null
          })
          .then((result) => {
            console.log('[MASTER] pipeline OK -> broadcast master-received', {
              originalOxId: data.message?.oxId,
              finalOxId: result.payload.oxId,
              reassigned: result.reassigned,
              sisbov: result.payload.sisbov
            });
            setLastOxManaged();
            window.electronAPI.webSocket.sendCommand('master-received', result.payload);
          })
          .catch((error) => {
            const code =
              error instanceof NoAnimalAvailableError ? NO_ANIMAL_AVAILABLE_CODE : (error as { code?: string })?.code;

            console.error('[MASTER] pipeline FALHOU -> broadcast master-error', {
              sisbov: data.message?.sisbov,
              electronic: data.message?.electronic,
              code,
              error: error?.message
            });
            window.electronAPI.webSocket.sendCommand('master-error', {
              sisbov: data.message?.sisbov ?? null,
              electronic: data.message?.electronic ?? null,
              code: code ?? null,
              message: error?.message || 'Erro ao processar identificação'
            });
          });

        return;
      }
    },
    [syncServiceRef, setLastOxManaged]
  );

  useEffect(() => {
    syncServiceRef.current = new DataSyncService({
      onProcessingChange: () => {},
      batchSize: 1,
      intervalMs: 1000
    });

    window.electronAPI.webSocket.getStatus().then((value) => {
      setStatus(value);
    });

    window.electronAPI.webSocket.onMessageReceived((messageData: any) => {
      onMessageReceived(messageData);
    });

    return () => {
      stopPingPong();
      window.electronAPI.webSocket.removeMessageReceivedListener();
    };
  }, [stopPingPong]);

  return <Context.Provider value={{ onSubmit }}>{children}</Context.Provider>;
}

export function useBroadcastPersonalContext() {
  const context = useContext(Context);

  if (!context) {
    throw new Error('useBroadcastPersonal must be used within a BroadcastPersonalProvider');
  }

  return context;
}
